<template>
  <div class="wdp" ref="root">
    <button
      type="button"
      class="wdp-field"
      :class="{ 'wdp-field--disabled': disabled, 'wdp-field--open': open }"
      :disabled="disabled"
      @click="toggle"
    >
      <i class="bx bx-calendar wdp-field-icon"></i>
      <span v-if="displayValue" class="wdp-value">{{ displayValue }}</span>
      <span v-else class="wdp-placeholder">{{ placeholder }}</span>
      <i class="bx bx-chevron-down wdp-field-caret"></i>
    </button>

    <!-- Teleported so scrollable parents (modal bodies, cards) cannot clip the calendar -->
    <Teleport to="body">
      <div v-if="open" ref="panel" class="wdp-panel" :style="panelStyle">
        <div class="wdp-panel-head">
          <button type="button" class="wdp-nav" title="Previous month" @click="shiftMonth(-1)">
            <i class="bx bx-chevron-left"></i>
          </button>
          <span class="wdp-month-label">{{ monthLabel }}</span>
          <button type="button" class="wdp-nav" title="Next month" @click="shiftMonth(1)">
            <i class="bx bx-chevron-right"></i>
          </button>
        </div>

        <div class="wdp-weekdays">
          <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
        </div>

        <div class="wdp-grid">
          <span v-for="blank in leadingBlanks" :key="'blank-' + blank" class="wdp-cell wdp-cell--empty"></span>
          <button
            v-for="cell in monthCells"
            :key="cell.key"
            type="button"
            class="wdp-cell"
            :class="{
              'wdp-cell--weekend': cell.isWeekend,
              'wdp-cell--holiday': cell.isHoliday,
              'wdp-cell--disabled': cell.disabled,
              'wdp-cell--selected': cell.key === selectedKey,
              'wdp-cell--today': cell.key === todayKey
            }"
            :disabled="cell.disabled"
            :title="cell.title"
            @click="selectDate(cell)"
          >
            {{ cell.day }}
          </button>
        </div>

        <div class="wdp-legend">
          <span><i class="wdp-dot wdp-dot--weekend"></i>Weekend</span>
          <span><i class="wdp-dot wdp-dot--holiday"></i>Holiday</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
/* eslint-disable */
import {
  holidayMap,
  holidayName,
  isHolidayKey,
  isWeekendKey,
  loadHolidayYear,
  parseDateKey,
  toDateKey
} from "@/utils/leaveCalendar";

export default {
  name: "WorkingDatePicker",
  props: {
    modelValue: { type: String, default: "" },
    min: { type: String, default: "" },
    max: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select a working day" }
  },
  emits: ["update:modelValue", "change"],
  data() {
    const base = parseDateKey(this.modelValue) || new Date();
    return {
      open: false,
      panelStyle: { top: "0px", left: "0px" },
      viewYear: base.getFullYear(),
      viewMonth: base.getMonth(),
      todayKey: toDateKey(new Date()),
      weekdayLabels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    };
  },
  computed: {
    selectedKey() {
      return toDateKey(this.modelValue);
    },
    displayValue() {
      const d = parseDateKey(this.modelValue);
      if (!d) return "";
      return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    },
    monthLabel() {
      return new Date(this.viewYear, this.viewMonth, 1)
        .toLocaleDateString("en-US", { month: "long", year: "numeric" });
    },
    leadingBlanks() {
      const first = new Date(this.viewYear, this.viewMonth, 1).getDay();
      return Array.from({ length: first }, (_, i) => i);
    },
    monthCells() {
      // holidayMap is reactive, so cells refresh once holidays finish loading
      const holidays = holidayMap;
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const cells = [];

      for (let day = 1; day <= daysInMonth; day++) {
        const key = toDateKey(new Date(this.viewYear, this.viewMonth, day));
        const weekend = isWeekendKey(key);
        const holiday = Boolean(holidays[key]) || isHolidayKey(key);
        // Booleans must be real booleans: Vue renders disabled="" as disabled
        const outOfRange = Boolean(
          (this.min && key < this.min) || (this.max && key > this.max)
        );

        cells.push({
          key,
          day,
          isWeekend: weekend,
          isHoliday: holiday,
          disabled: Boolean(weekend || holiday || outOfRange),
          title: holiday
            ? holidayName(key) || "Public holiday"
            : (weekend ? "Weekend" : (outOfRange ? "Outside the allowed range" : ""))
        });
      }
      return cells;
    }
  },
  watch: {
    modelValue(value) {
      const d = parseDateKey(value);
      if (d) {
        this.viewYear = d.getFullYear();
        this.viewMonth = d.getMonth();
      }
    },
    viewYear() {
      this.ensureHolidays();
    }
  },
  mounted() {
    this.ensureHolidays();
    // Capture phase: ancestors using @click.stop (modals) must not swallow outside clicks
    document.addEventListener("click", this.handleOutsideClick, true);
    document.addEventListener("keydown", this.handleEscape);
    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("scroll", this.updatePosition, true);
  },
  unmounted() {
    document.removeEventListener("click", this.handleOutsideClick, true);
    document.removeEventListener("keydown", this.handleEscape);
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("scroll", this.updatePosition, true);
  },
  methods: {
    ensureHolidays() {
      loadHolidayYear(this.viewYear);
      loadHolidayYear(this.viewYear + 1);
    },
    toggle() {
      if (this.open) {
        this.close();
        return;
      }
      this.open = true;
      this.ensureHolidays();
      this.updatePosition();
    },
    updatePosition() {
      if (!this.open) return;
      const rect = this.$refs.root?.getBoundingClientRect();
      if (!rect) return;

      const panelWidth = 290;
      const panelHeight = 340;
      const margin = 8;

      let left = Math.min(rect.left, window.innerWidth - panelWidth - margin);
      left = Math.max(margin, left);

      let top = rect.bottom + 6;
      if (top + panelHeight > window.innerHeight - margin && rect.top > panelHeight) {
        top = rect.top - panelHeight - 6;
      }

      this.panelStyle = { top: `${Math.round(top)}px`, left: `${Math.round(left)}px` };
    },
    close() {
      this.open = false;
    },
    handleOutsideClick(event) {
      if (!this.open) return;
      const insideField = this.$refs.root && this.$refs.root.contains(event.target);
      const insidePanel = this.$refs.panel && this.$refs.panel.contains(event.target);
      if (!insideField && !insidePanel) {
        this.close();
      }
    },
    handleEscape(event) {
      if (event.key === "Escape") this.close();
    },
    shiftMonth(step) {
      const next = new Date(this.viewYear, this.viewMonth + step, 1);
      this.viewYear = next.getFullYear();
      this.viewMonth = next.getMonth();
    },
    selectDate(cell) {
      if (cell.disabled) return;
      this.close();
      this.$emit("update:modelValue", cell.key);
      this.$emit("change", cell.key);
    }
  }
};
</script>

<style scoped>
.wdp {
  position: relative;
}

.wdp-field {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 14px;
  font-family: inherit;
  color: #1e293b;
  cursor: pointer;
  text-align: left;
}

.wdp-field--open,
.wdp-field:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.wdp-field--disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

.wdp-field-icon {
  color: #4f46e5;
  font-size: 18px;
}

.wdp-field-caret {
  margin-left: auto;
  color: #94a3b8;
}

.wdp-value {
  font-weight: 500;
}

.wdp-placeholder {
  color: #94a3b8;
}

.wdp-panel {
  position: fixed;
  z-index: 3000;
  width: 290px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
}

.wdp-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.wdp-month-label {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.wdp-nav {
  width: 28px;
  height: 28px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wdp-nav:hover {
  background: #f1f5f9;
}

.wdp-weekdays,
.wdp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.wdp-weekdays span {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  padding-bottom: 4px;
}

.wdp-cell {
  height: 34px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: #1e293b;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
}

.wdp-cell:hover:not(.wdp-cell--disabled) {
  background: #eef2ff;
}

.wdp-cell--empty {
  background: transparent;
  cursor: default;
}

.wdp-cell--today {
  box-shadow: inset 0 0 0 1px #4f46e5;
}

.wdp-cell--selected {
  background: #4f46e5;
  color: #fff;
}

.wdp-cell--weekend,
.wdp-cell--disabled {
  background: #f1f5f9;
  color: #cbd5e1;
  cursor: not-allowed;
  text-decoration: line-through;
}

.wdp-cell--holiday {
  background: #fef3c7;
  color: #d1a054;
}

.wdp-legend {
  display: flex;
  gap: 14px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
  font-size: 11px;
  color: #64748b;
}

.wdp-legend span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.wdp-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  display: inline-block;
}

.wdp-dot--weekend {
  background: #e2e8f0;
}

.wdp-dot--holiday {
  background: #fcd34d;
}
</style>
